// Device glyphs (§8: "on-screen glyphs switch to the device used most recently"). Pure data: the
// UI (WP9b) renders a Glyph as a key cap, mouse icon, face button, shoulder/trigger pill, D-pad
// arrow or touch-button name. Gamepad labels follow the pad family detected from the id string
// (§8.2): Xbox, PlayStation and Switch Pro; anything else uses the Xbox set.
import type { Bindings, KeyAction, PadAction } from './bindings';

export type InputDevice = 'kbm' | 'pad' | 'touch';
export type PadFamily = 'xbox' | 'playstation' | 'switch' | 'generic';

/** Everything a prompt, tooltip or settings row may need a glyph for. */
export type GlyphAction =
  | 'move' | 'aim' | 'jump' | 'shove' | 'grab' | 'throw' | 'item' | 'honk' | 'emote'
  | 'emote1' | 'emote2' | 'emote3' | 'emote4' | 'scoreboard' | 'menu' | 'ready'
  | 'confirm' | 'back' | 'tabL' | 'tabR' | 'mash';

export type GlyphKind = 'key' | 'mouse' | 'face' | 'shoulder' | 'trigger' | 'dpad' | 'stick' | 'system' | 'touch' | 'none';

export interface Glyph {
  device: InputDevice;
  kind: GlyphKind;
  /** Short cap text: 'W', 'Space', 'LMB', 'A', '✕', 'LB', 'ZR', 'BONK'. */
  label: string;
  /** Accessible / plain-text form, e.g. '[LMB]', '(A)', 'BONK'. */
  text: string;
  /** Face-button tint (PlayStation shapes, Xbox letters), CSS color, when the family has one. */
  color?: string;
}

// ---- Keyboard + mouse -------------------------------------------------------------------------

const KEY_NAMES: Readonly<Record<string, string>> = {
  Space: 'Space', Enter: 'Enter', NumpadEnter: 'Enter', Escape: 'Esc', Tab: 'Tab', Backspace: 'Bksp',
  ShiftLeft: 'L-Shift', ShiftRight: 'R-Shift', ControlLeft: 'L-Ctrl', ControlRight: 'R-Ctrl',
  AltLeft: 'L-Alt', AltRight: 'R-Alt', MetaLeft: 'L-Meta', MetaRight: 'R-Meta', CapsLock: 'Caps',
  ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→',
  Backquote: '`', Minus: '-', Equal: '=', BracketLeft: '[', BracketRight: ']', Backslash: '\\',
  Semicolon: ';', Quote: "'", Comma: ',', Period: '.', Slash: '/', IntlBackslash: '\\',
  Insert: 'Ins', Delete: 'Del', Home: 'Home', End: 'End', PageUp: 'PgUp', PageDown: 'PgDn',
  Mouse0: 'LMB', Mouse1: 'MMB', Mouse2: 'RMB', Mouse3: 'M4', Mouse4: 'M5',
};

/** Short label for a `KeyboardEvent.code` or 'Mouse0'–'Mouse4' ('KeyW' → 'W', 'Digit1' → '1'). */
export function keyLabel(code: string | null): string {
  if (code === null) return '—';
  const named = KEY_NAMES[code];
  if (named) return named;
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return `Num ${code.slice(6)}`;
  if (/^F\d{1,2}$/.test(code)) return code;
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  return code;
}

export function isMouseCode(code: string | null): boolean {
  return code !== null && code.startsWith('Mouse');
}

// ---- Gamepad ----------------------------------------------------------------------------------

/** Detects the label family from `Gamepad.id` (vendor ids: Microsoft 045e, Sony 054c, Nintendo 057e). */
export function padFamilyFromId(id: string): PadFamily {
  const s = id.toLowerCase();
  // Vendor ids first, then brand names; a bare "Wireless Controller" is how some platforms name a
  // DualShock 4, but "Xbox Wireless Controller" must stay Xbox.
  if (/\b054c\b/.test(s)) return 'playstation';
  if (/\b057e\b/.test(s)) return 'switch';
  if (/\b045e\b/.test(s)) return 'xbox';
  if (/xbox|x-box|xinput|microsoft/.test(s)) return 'xbox';
  if (/playstation|dualshock|dualsense|\bps[345]\b|sony/.test(s)) return 'playstation';
  if (/nintendo|switch|pro controller|joy-con/.test(s)) return 'switch';
  if (/wireless controller/.test(s)) return 'playstation';
  return 'generic';
}

interface PadLabel { label: string; kind: GlyphKind; color?: string }

const XBOX: readonly PadLabel[] = [
  { label: 'A', kind: 'face', color: '#3DDC97' }, { label: 'B', kind: 'face', color: '#FF5A4E' },
  { label: 'X', kind: 'face', color: '#4CB8FF' }, { label: 'Y', kind: 'face', color: '#FFC93C' },
  { label: 'LB', kind: 'shoulder' }, { label: 'RB', kind: 'shoulder' }, { label: 'LT', kind: 'trigger' }, { label: 'RT', kind: 'trigger' },
  { label: 'View', kind: 'system' }, { label: 'Menu', kind: 'system' }, { label: 'LS', kind: 'stick' }, { label: 'RS', kind: 'stick' },
  { label: '↑', kind: 'dpad' }, { label: '↓', kind: 'dpad' }, { label: '←', kind: 'dpad' }, { label: '→', kind: 'dpad' }, { label: 'Guide', kind: 'system' },
];
const PLAYSTATION: readonly PadLabel[] = [
  { label: '✕', kind: 'face', color: '#7EA6FF' }, { label: '○', kind: 'face', color: '#FF6B7A' },
  { label: '□', kind: 'face', color: '#F59BD9' }, { label: '△', kind: 'face', color: '#3DDCB4' },
  { label: 'L1', kind: 'shoulder' }, { label: 'R1', kind: 'shoulder' }, { label: 'L2', kind: 'trigger' }, { label: 'R2', kind: 'trigger' },
  { label: 'Share', kind: 'system' }, { label: 'Options', kind: 'system' }, { label: 'L3', kind: 'stick' }, { label: 'R3', kind: 'stick' },
  { label: '↑', kind: 'dpad' }, { label: '↓', kind: 'dpad' }, { label: '←', kind: 'dpad' }, { label: '→', kind: 'dpad' }, { label: 'PS', kind: 'system' },
];
// Switch Pro in Standard mapping: index 0 is the bottom button (labelled B), 1 the right one (A).
const SWITCH: readonly PadLabel[] = [
  { label: 'B', kind: 'face' }, { label: 'A', kind: 'face' }, { label: 'Y', kind: 'face' }, { label: 'X', kind: 'face' },
  { label: 'L', kind: 'shoulder' }, { label: 'R', kind: 'shoulder' }, { label: 'ZL', kind: 'trigger' }, { label: 'ZR', kind: 'trigger' },
  { label: '−', kind: 'system' }, { label: '+', kind: 'system' }, { label: 'LS', kind: 'stick' }, { label: 'RS', kind: 'stick' },
  { label: '↑', kind: 'dpad' }, { label: '↓', kind: 'dpad' }, { label: '←', kind: 'dpad' }, { label: '→', kind: 'dpad' }, { label: 'Home', kind: 'system' },
];

function padTable(family: PadFamily): readonly PadLabel[] {
  return family === 'playstation' ? PLAYSTATION : family === 'switch' ? SWITCH : XBOX;
}

/** Label for a Standard-mapping button index in a pad family. */
export function padButtonLabel(index: number, family: PadFamily): string {
  return padTable(family)[index]?.label ?? `B${index}`;
}

function padGlyph(index: number, family: PadFamily): Glyph {
  const p = padTable(family)[index];
  if (!p) return { device: 'pad', kind: 'face', label: `B${index}`, text: `(B${index})` };
  const g: Glyph = { device: 'pad', kind: p.kind, label: p.label, text: `(${p.label})` };
  if (p.color) g.color = p.color;
  return g;
}

// ---- Touch ------------------------------------------------------------------------------------

const TOUCH_NAMES: Readonly<Partial<Record<GlyphAction, string>>> = {
  move: 'STICK', aim: 'STICK', jump: 'JUMP', shove: 'BONK', grab: 'GRAB', throw: 'THROW', item: 'ITEM', honk: 'HONK',
  emote: 'EMOTE', emote1: 'EMOTE', emote2: 'EMOTE', emote3: 'EMOTE', emote4: 'EMOTE', menu: 'PAUSE', mash: 'MASH!',
  confirm: 'TAP', back: 'BACK', ready: 'READY', scoreboard: 'SCORES', tabL: '◀', tabR: '▶',
};

// ---- Resolution -------------------------------------------------------------------------------

const KEY_FOR: Readonly<Partial<Record<GlyphAction, KeyAction>>> = {
  jump: 'jump', shove: 'shove', grab: 'grab', throw: 'grab', mash: 'shove', item: 'item', honk: 'honk',
  emote: 'emote1', emote1: 'emote1', emote2: 'emote2', emote3: 'emote3', emote4: 'emote4',
  scoreboard: 'scoreboard', menu: 'menu', ready: 'ready', confirm: 'ready', back: 'menu',
};
const PAD_FOR: Readonly<Partial<Record<GlyphAction, PadAction>>> = {
  jump: 'jump', shove: 'shove', grab: 'grab', throw: 'grab', mash: 'shove', item: 'item', honk: 'honk',
  emote: 'emote1', emote1: 'emote1', emote2: 'emote2', emote3: 'emote3', emote4: 'emote4', scoreboard: 'scoreboard', menu: 'menu',
};
/** Menu buttons are fixed (§8.2): A confirms, B goes back, LB/RB switch tabs, Start = menu. */
const PAD_FIXED: Readonly<Partial<Record<GlyphAction, number>>> = { confirm: 0, ready: 0, back: 1, tabL: 4, tabR: 5 };

/**
 * The glyph for `action` on `device`, from the live bindings (primary slot first). `padMap` maps
 * a bound button to the physical one (the Swap Jump/Grab setting).
 */
export function glyphFor(action: GlyphAction, device: InputDevice, b: Readonly<Bindings>, family: PadFamily, padMap: (btn: number) => number = (n) => n): Glyph {
  if (device === 'touch') {
    const label = TOUCH_NAMES[action] ?? action.toUpperCase();
    return { device, kind: 'touch', label, text: label };
  }
  if (device === 'pad') {
    if (action === 'move') return { device, kind: 'stick', label: 'LS', text: '(LS)' };
    if (action === 'aim') return { device, kind: 'stick', label: 'RS', text: '(RS)' };
    const fixed = PAD_FIXED[action];
    if (fixed !== undefined) return padGlyph(fixed, family);
    const pa = PAD_FOR[action];
    const btn = pa ? b.pad[pa][0] : undefined;
    return btn === undefined ? { device, kind: 'none', label: '—', text: '(—)' } : padGlyph(padMap(btn), family);
  }
  if (action === 'move') {
    const k = b.keys;
    const label = [k.up[0], k.left[0], k.down[0], k.right[0]].map(keyLabel).join('');
    return { device, kind: 'key', label, text: `[${label}]` };
  }
  if (action === 'aim') return { device, kind: 'mouse', label: 'Mouse', text: '[Mouse]' };
  if (action === 'tabL') return { device, kind: 'key', label: 'Q', text: '[Q]' };
  if (action === 'tabR') return { device, kind: 'key', label: 'E', text: '[E]' };
  const ka = KEY_FOR[action];
  if (!ka) return { device, kind: 'none', label: '—', text: '[—]' };
  const [p, s] = b.keys[ka];
  const code = p ?? s;
  const label = keyLabel(code);
  return { device, kind: isMouseCode(code) ? 'mouse' : code === null ? 'none' : 'key', label, text: `[${label}]` };
}
